/**
 * FlashSaleQueue — la Sala de Espera montada como ruta real (`/flash-sales/:id/fila`).
 *
 * Es el paso intermedio entre el detalle de la promo y la compra: el fan entra
 * acá, el motor de `api-core` le asigna turno, y al asignárselo lo devuelve al
 * detalle con el turno vigente para completar la compra.
 *
 * Vive como página propia y no como estado del detalle a propósito: así la fila
 * tiene URL, sobrevive un refresco (el ticket es del backend, no del componente)
 * y la guarda de `beforeunload` tiene algo concreto que proteger.
 *
 * **Sin sesión no hay fila.** El motor responde 401 y acá se redirige a `/login`
 * en vez de mostrar la sala: enseñarle "vas en fila, 1 s" a alguien que no está
 * en ninguna fila es mentirle. El destino de vuelta viaja en `?from=`, que es el
 * parámetro que el Login ya sabe leer (con su allowlist anti open-redirect).
 */
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { WaitingRoom } from '@/components/flash-sales/WaitingRoom';
import { useFlashSales } from '@/hooks/useFlashSales';
import { useFlashSaleQueue } from '@/hooks/useFlashSaleQueue';

export function FlashSaleQueuePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: sales } = useFlashSales();

  const sale = sales?.find((s) => s.id === id) ?? null;

  // Turno asignado → de vuelta al detalle, que ya podrá cerrar la compra.
  const handleReady = useCallback(() => {
    if (!id) return;
    navigate(`/flash-sales/${id}?turno=1`, { replace: true });
  }, [id, navigate]);

  // La fila se consulta ACÁ y el resultado se le pasa a la sala. Antes el
  // componente llamaba al hook por su cuenta, y por eso no había forma de
  // reaccionar al 401 desde la ruta.
  const queue = useFlashSaleQueue({ flashSaleId: id, onReady: handleReady });

  // Sin sesión → al login, con retorno a esta misma fila. `replace` para que el
  // botón "atrás" no lo devuelva a una sala que no puede usar.
  useEffect(() => {
    if (!queue.isUnauthorized || !id) return;
    const from = encodeURIComponent(`/flash-sales/${id}/fila`);
    navigate(`/login?from=${from}`, { replace: true });
  }, [queue.isUnauthorized, id, navigate]);

  if (!id) {
    return (
      <section className="container-fc py-12">
        <p className="text-fc-muted">No encontramos la venta.</p>
      </section>
    );
  }

  // Mientras se resuelve la redirección no se pinta la sala: un parpadeo de
  // "vas en fila" seguiría siendo mentira, aunque dure 200 ms.
  if (queue.isUnauthorized) return <section className="container-fc py-12" />;

  return (
    <section className="container-fc py-10">
      <WaitingRoom queue={queue} eventTitle={sale?.title} />
    </section>
  );
}

export default FlashSaleQueuePage;
